import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow, format } from "date-fns";
import { Sparkles, Loader2, CheckCircle2, AlertTriangle, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { generateDigest, listDigests, getDigest } from "@/lib/digests.functions";

export const Route = createFileRoute("/_authenticated/digests")({
  component: DigestsPage,
});

function DigestsPage() {
  const qc = useQueryClient();
  const fetchList = useServerFn(listDigests);
  const fetchDetail = useServerFn(getDigest);
  const generate = useServerFn(generateDigest);

  const [period, setPeriod] = useState<"daily" | "weekly">("daily");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const list = useQuery({ queryKey: ["digests"], queryFn: () => fetchList() });
  const detail = useQuery({
    queryKey: ["digest", selectedId],
    queryFn: () => fetchDetail({ data: { id: selectedId! } }),
    enabled: !!selectedId,
  });

  const gen = useMutation({
    mutationFn: () => generate({ data: { period } }),
    onSuccess: (res) => {
      toast.success("Digest generated");
      qc.invalidateQueries({ queryKey: ["digests"] });
      setSelectedId(res.digest_id);
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed"),
  });

  const digests = list.data?.digests ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Smart Digests</h1>
          <p className="text-sm text-muted-foreground">
            Periodic briefs across every workspace. Open anomalies, MRR snapshot, recently resolved.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={(v) => setPeriod(v as any)}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => gen.mutate()} disabled={gen.isPending}>
            {gen.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="mr-2 h-4 w-4" />
            )}
            Generate digest
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card">
        {list.isLoading && (
          <div className="p-6 text-sm text-muted-foreground">Loading…</div>
        )}
        {!list.isLoading && digests.length === 0 && (
          <div className="p-10 text-center">
            <Sparkles className="mx-auto h-8 w-8 text-muted-foreground/50" />
            <p className="mt-3 text-sm font-medium">No digests yet</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Generate one now, or wait for the scheduled daily run.
            </p>
          </div>
        )}
        {digests.map((d: any) => (
          <button
            key={d.id}
            onClick={() => setSelectedId(d.id)}
            className="flex w-full items-start gap-3 border-b border-border p-4 text-left transition-colors last:border-b-0 hover:bg-accent/50"
          >
            <div className="mt-0.5">
              {d.read_at ? (
                <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
              ) : (
                <div className="h-2 w-2 rounded-full bg-primary" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs capitalize">
                  {d.period}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {formatDistanceToNow(new Date(d.generated_at), { addSuffix: true })}
                </span>
              </div>
              <p className="mt-1.5 text-sm font-medium leading-snug">{d.headline}</p>
              <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{d.summary_body}</p>
            </div>
          </button>
        ))}
      </div>

      <Sheet open={!!selectedId} onOpenChange={(o) => !o && setSelectedId(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
          {detail.data && <DigestDetail digest={detail.data.digest} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function DigestDetail({ digest }: { digest: any }) {
  const sections = (digest.sections_json ?? []) as any[];
  const openSec = sections.find((s) => s.kind === "open_anomalies");
  const deltaSec = sections.find((s) => s.kind === "workspace_deltas");
  const resolvedSec = sections.find((s) => s.kind === "resolved");

  return (
    <div className="space-y-6">
      <SheetHeader>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="capitalize">
            {digest.period}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {format(new Date(digest.period_start), "MMM d")} – {format(new Date(digest.period_end), "MMM d, yyyy")}
          </span>
        </div>
        <SheetTitle className="text-left text-lg leading-snug">{digest.headline}</SheetTitle>
        <SheetDescription className="text-left whitespace-pre-line text-sm">
          {digest.summary_body}
        </SheetDescription>
      </SheetHeader>

      {deltaSec?.items?.length > 0 && (
        <section>
          <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <TrendingUp className="h-3.5 w-3.5" /> Workspace snapshot
          </h3>
          <div className="space-y-2">
            {deltaSec.items.map((d: any) => (
              <div
                key={d.workspace_id}
                className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2 text-sm"
              >
                <span className="font-medium">{d.program_name}</span>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>MRR ${Number(d.mrr).toFixed(2)}</span>
                  <span>{d.active_monthly} active</span>
                  {d.at_risk > 0 && <span className="text-destructive">{d.at_risk} at risk</span>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {openSec?.items?.length > 0 && (
        <section>
          <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <AlertTriangle className="h-3.5 w-3.5" /> Open anomalies ({openSec.items.length})
          </h3>
          <div className="space-y-2">
            {openSec.items.map((a: any) => (
              <div key={a.id} className="rounded-lg border border-border bg-background p-3">
                <div className="flex items-start gap-2">
                  <Badge variant={a.severity === "critical" || a.severity === "high" ? "destructive" : "secondary"} className="text-xs">
                    {a.severity}
                  </Badge>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm leading-snug">{a.title}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{a.workspace}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {resolvedSec?.items?.length > 0 && (
        <section>
          <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <CheckCircle2 className="h-3.5 w-3.5" /> Recently resolved ({resolvedSec.items.length})
          </h3>
          <div className="space-y-1.5">
            {resolvedSec.items.map((a: any) => (
              <div key={a.id} className="rounded-lg border border-border bg-background px-3 py-2 text-sm">
                <p className="leading-snug">{a.title}</p>
                <p className="text-xs text-muted-foreground">{a.workspace}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
